import { Column } from 'primereact/column'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { OwnFuelStationsService } from '../../../services/own-fuel-stations/ownFuelStations.service'
import { propsInputNumberCurrency } from '../../../utils/constants/common'
import { GROUPS, MODULES } from '../../../utils/constants/roles'
import auxFunction from '../../../utils/helpers/auxFun'
import { DataTableFun } from '../../../utils/helpers/dataTableFun'
import useAccess from '../../../utils/hooks/useAccess'
import DataTableComp from '../../common/data-table/DataTableComp'
import editCalendarRowColumn from '../../common/data-table/editors/editCalendarRowColumn'
import editNumberRowColumn from '../../common/data-table/editors/editNumberRowColumn'
import editPriceRowColumn from '../../common/data-table/editors/editPriceRowColumn'
import DropdownComp from '../../common/form-components/dropdown/DropdownComp'
import {
  formatFuelQuantity,
  formatCurrency,
} from '../../../utils/helpers/common'

const ShoppingList = ({ dataFacade, suppliers, pagerOptions }) => {
  const { formatMessage: msg } = useIntl()
  const { canAdd, canDelete, canEdit } = useAccess()

  const [centers, setCenters] = useState([])

  const currentDate = new Date()

  useEffect(() => {
    getCenters()
  }, [dataFacade?.data?.resp])

  const getCenters = async () => {
    const resp = await OwnFuelStationsService.getAll({})
    if (resp) {
      setCenters(resp?.resp)
    }
  }

  const onCellEditComplete = async (e) => {
    const { rowData, newValue, newRowData, field, originalEvent: event } = e
    const newField = field === 'centerName' ? 'ownStation' : field
    if (newField === 'ownStation') {
      newRowData[newField] = centers.find(
        (c) => c.centerName === newValue || c._id === newValue,
      )?._id
    } else if (newValue !== null) newRowData[newField] = newValue
    else event.preventDefault()
    await dataFacade.edit({ _id: rowData?._id, body: newRowData })
  }

  const handleDuplicate = async (id) => {
    await dataFacade?.otherCreate(id)
  }

  const editorCenters = (rowData) => {
    return (
      <DropdownComp
        value={rowData?.value}
        options={centers}
        optionLabel="centerName"
        optionValue="_id"
        onChange={({ target: { value } }) => rowData.editorCallback(value)}
      />
    )
  }

  const editorSuppliers = (rowData) => {
    return (
      <DropdownComp
        value={rowData?.value}
        options={suppliers}
        optionLabel="name"
        optionValue="_id"
        onChange={({ target: { value } }) => rowData.editorCallback(value)}
      />
    )
  }

  // Function to get center name for sorting
  const getCenterName = (ownStation) => {
    const center = centers.find((c) => c._id === ownStation)
    return center ? center.centerName : ''
  }

  return (
    <DataTableComp
      pagerOptions={pagerOptions}
      tableOptions={{
        value: dataFacade?.data?.resp?.map((item) => ({
          ...item,
          centerName: getCenterName(item.ownStation),
        })),
        editMode: 'cell',
        ...dataFacade?.sortOptions,
      }}
      columns={[
        <Column field="num" sortable />,
        <Column
          field="downloadDate"
          header={msg({ id: 'date.download' })}
          body={(rowEditor) => auxFunction.parseDate(rowEditor?.downloadDate)}
          {...editCalendarRowColumn({
            dateFormat: 'yy',
            view: 'month',
            minDate: currentDate,
          })}
          onCellEditComplete={(e) => onCellEditComplete(e)}
          sortable
        />,
        <Column
          field="centerName" // Use virtual field for sorting
          body={(rowData) => rowData?.centerName}
          editor={(rowData) => editorCenters(rowData)}
          onCellEditComplete={(e) => onCellEditComplete(e)}
          header={msg({ id: 'gas.station' })}
          sortable
        />,
        <Column
          field="supplierGOA"
          body={(rowData) => rowData?.supplierGOAName}
          editor={(rowData) => editorSuppliers(rowData)}
          onCellEditComplete={(e) => onCellEditComplete(e)}
          style={{ textAlign: 'right' }}
          header={msg({ id: 'supplierGOA' })}
        />,
        <Column
          field="supplierG95"
          body={(rowData) => rowData?.supplierG95Name}
          editor={(rowData) => editorSuppliers(rowData)}
          onCellEditComplete={(e) => onCellEditComplete(e)}
          style={{ textAlign: 'right' }}
          header={msg({ id: 'supplierG95' })}
        />,
        <Column
          field="quantity.GOA"
          header={msg({ id: 'quantity.GOA' })}
          {...editNumberRowColumn({})}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          style={{ textAlign: 'right' }}
          body={(rowData) => formatFuelQuantity(rowData.quantity.GOA, true)}
          sortable
        />,
        <Column
          field="quantity.G95"
          header={msg({ id: 'quantity.G95' })}
          {...editNumberRowColumn({})}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          style={{ textAlign: 'right' }}
          body={(rowData) => formatFuelQuantity(rowData.quantity.G95, true)}
          sortable
        />,
        <Column
          field="quantity.GOAPLUS"
          header={msg({ id: 'quantity.GOAPLUS' })}
          {...editNumberRowColumn({})}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          style={{ textAlign: 'right' }}
          body={(rowData) => formatFuelQuantity(rowData.quantity.GOAPLUS, true)}
          sortable
        />,
        <Column
          field="prices.GOA.price"
          header={msg({ id: 'price.GOA' })}
          {...editPriceRowColumn({})}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          style={{ textAlign: 'right' }}
          body={(rowData) =>
            formatCurrency(rowData?.prices?.GOA?.price, {}, true)
          }
          sortable
        />,
        <Column
          field="prices.G95.price"
          header={msg({ id: 'price.G95' })}
          {...editPriceRowColumn({})}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          style={{ textAlign: 'right' }}
          body={(rowData) =>
            formatCurrency(rowData?.prices?.G95?.price, {}, true)
          }
          sortable
        />,
        <Column
          field="transporter.price"
          header={msg({ id: 'transporter.price' })}
          {...editNumberRowColumn({
            propsInput: {
              ...propsInputNumberCurrency,
              minFractionDigits: '2',
              maxFractionDigits: '2',
            },
          })}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          style={{ textAlign: 'right' }}
          body={(rowData) =>
            formatCurrency(rowData?.transporter?.price, {}, true)
          }
          sortable
        />,
        <Column
          field="transporter.email"
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          header={msg({ id: 'transporter.email' })}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          sortable
        />,
        <Column
          field="remarks"
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          header={msg({ id: 'remarks' })}
          onCellEditComplete={(rowData) => onCellEditComplete(rowData)}
          sortable
        />,
        canAdd(MODULES.PURCHASE_FUEL, GROUPS.MADE) && (
          <Column
            className="row-add"
            align="right"
            body={(rowData) =>
              DataTableFun.buttonDuplicate(rowData, handleDuplicate)
            }
          />
        ),
        canDelete(MODULES.PURCHASE_FUEL, GROUPS.MADE) && (
          <Column
            className="row-delete"
            align="right"
            body={(rowData) =>
              DataTableFun.buttonDelete(rowData, dataFacade?.remove)
            }
          />
        ),
        canEdit(MODULES.PURCHASE_FUEL, GROUPS.MADE) && (
          <Column className="row-edit" align="right" rowEditor />
        ),
      ]}
    />
  )
}

export default ShoppingList
